/**
 * Section Time Deltas
 *
 * Cuts the track into corner-complex sections and attributes net time
 * gained/lost per section from time-drift results. A section spans from the
 * midpoint of one straight to the midpoint of the next (formulated circularly,
 * so a complex spanning start/finish becomes a "wrap" section). Cuts sit
 * mid-straight where the drift curve is flat, so a local gain that causes a
 * bigger downstream loss (brake later -> lose the exit) nets out inside one
 * section instead of showing as a false gain.
 *
 * Open-closed over zone detection: consumes TrackZone[] without modifying it.
 * Spec: docs/superpowers/specs/2026-07-28-section-time-delta-badges-design.md
 */

import { type TrackZone } from '../../analysis/zones/TrackZoneTypes'
import { type TimeDriftResult } from '../../domain/lap/TimeDriftCalculator'
import { interpolateTimeDrift } from './timeDriftInterpolation'

/** A corner-complex section between two mid-straight cuts. */
export interface TimeSection {
  id: string
  /** Cut before the complex, meters from lap start. */
  startDistance: number
  /** Cut after the complex. Less than startDistance when the section wraps. */
  endDistance: number
  /** Non-straight zones inside the section, in track order. */
  zoneIds: string[]
  /** True when the section crosses start/finish. */
  wraps: boolean
}

/** Circular membership: is `d` inside [start, end) going forward around the lap? */
function inSection(
  d: number,
  start: number,
  end: number,
  wraps: boolean
): boolean {
  if (!wraps) return d >= start && d < end
  return d >= start || d < end
}

/** A contiguous stretch of sustained time gain/loss on the drift curve. */
export interface RateHotspot {
  startDistance: number
  endDistance: number
}

export interface RateHotspotOptions {
  // Hotspot ATOMICITY inside buildTimeSections (whether interior zone cuts
  // are suppressed) is governed separately by MAX_ATOMIC_HOTSPOT_LENGTH_METERS.
  /** Sustained |slope| of the median drift curve that qualifies (ms per meter). */
  rateThresholdMsPerMeter: number
  /** Minimum hotspot length; shorter runs are noise. */
  minLengthMeters: number
  /** Sampling step for the median drift curve. */
  sampleStepMeters: number
  /** Same-sign hotspots closer than this merge into one. */
  mergeGapMeters: number
}

/** ~2 ms/m sustained renders solidly red/green on the drift gradient. */
export const DEFAULT_RATE_HOTSPOT_OPTIONS: RateHotspotOptions = {
  rateThresholdMsPerMeter: 2,
  minLengthMeters: 60,
  sampleStepMeters: 10,
  mergeGapMeters: 30,
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2
}

/**
 * Find stretches where the drift gradient itself pinpoints sustained
 * gain/loss ("very red/green" on the map). Works on the pointwise MEDIAN of
 * the comparison laps' smoothed drift curves (all share the reference lap's
 * distance frame), so a hotspot is where laps COMMONLY bleed time — with a
 * single comparison lap the median is just that lap's curve.
 */
export function findRateHotspots(
  drifts: ReadonlyMap<string, TimeDriftResult>,
  options: RateHotspotOptions = DEFAULT_RATE_HOTSPOT_OPTIONS
): RateHotspot[] {
  const curves = [...drifts.values()].filter((d) => d.points.length >= 2)
  if (curves.length === 0) return []
  const end = Math.min(
    ...curves.map((d) => d.points[d.points.length - 1].distanceMeters)
  )
  const step = options.sampleStepMeters
  if (end < 2 * step) return []

  const samples: number[] = []
  for (let d = 0; d <= end; d += step) {
    samples.push(median(curves.map((c) => interpolateTimeDrift(c, d))))
  }

  // Central-difference slope, classified into signed over-threshold runs.
  const runs: { start: number; end: number; sign: 1 | -1 }[] = []
  let current: { start: number; end: number; sign: 1 | -1 } | null = null
  for (let i = 1; i < samples.length - 1; i++) {
    const rate = (samples[i + 1] - samples[i - 1]) / (2 * step)
    let sign: 1 | -1 | 0 = 0
    if (rate >= options.rateThresholdMsPerMeter) sign = 1
    else if (rate <= -options.rateThresholdMsPerMeter) sign = -1

    if (sign === 0) {
      if (current !== null) runs.push(current)
      current = null
    } else if (current !== null && current.sign === sign) {
      current.end = i * step
    } else {
      if (current !== null) runs.push(current)
      current = { start: i * step, end: i * step, sign }
    }
  }
  if (current !== null) runs.push(current)

  // Merge same-sign neighbors across small gaps, then drop short runs.
  const merged: { start: number; end: number; sign: 1 | -1 }[] = []
  for (const run of runs) {
    const last = merged[merged.length - 1]
    if (
      last &&
      last.sign === run.sign &&
      run.start - last.end <= options.mergeGapMeters
    ) {
      last.end = run.end
    } else {
      merged.push({ ...run })
    }
  }
  return merged
    .filter((r) => r.end - r.start >= options.minLengthMeters)
    .map((r) => ({ startDistance: r.start, endDistance: r.end }))
}

/**
 * A hotspot only suppresses the zone cuts inside it up to this length.
 * Atomicity exists so a compact loss event isn't halved by a cut into two
 * sub-threshold invisible pieces — but when the comparison laps are slower
 * overall, huge stretches of the median drift curve sustain the hotspot
 * rate and merge into one mega-hotspot spanning several corners. Swallowing
 * the rider-granularity cuts there produced a single vague badge over
 * "acceleration, braking, corner, acceleration, corner, corner"; pieces of
 * a stretch that long each carry well over the significance floor anyway.
 */
export const MAX_ATOMIC_HOTSPOT_LENGTH_METERS = 250

/**
 * Cut the track at (a) the midpoint of every straight zone, (b) the start
 * of every braking zone entered directly from cornering or acceleration (no
 * straight between — common on tight tracks), and (c) the boundaries of any
 * rate hotspots. Hotspots up to MAX_ATOMIC_HOTSPOT_LENGTH_METERS are
 * ATOMIC: cuts of kind (a)/(b) that fall inside are removed, so a compact
 * sustained-loss stretch is exactly one section carrying its own number —
 * never split by a zone boundary it happens to cross, never diluted into
 * neighbors. Longer hotspots keep their boundary cuts but also keep the
 * zone cuts inside them (they span multiple rider events).
 *
 * Every cut-pair becomes a section (zone-less ones included) so the sections
 * form a full circular partition of the lap: section deltas always sum to
 * the lap's total delta, and no time is silently unattributed. Downstream
 * significance filtering (summarizeSectionDeltas) keeps the map uncluttered.
 *
 * Rule (b) keeps sections at rider granularity — "brake for T5 through the
 * exit run, until braking for T6" — without breaking the trap-netting: a
 * late-braking gain and the exit loss it causes still land in the same
 * section, because the braking zone OPENS its section. Linked corners with
 * no braking between them (chicanes, esses) still merge.
 */
export function buildTimeSections(
  zones: TrackZone[],
  hotspots: RateHotspot[] = []
): TimeSection[] {
  const sorted = [...zones].sort((a, b) => a.startDistance - b.startDistance)
  const straightCuts = sorted
    .filter((z) => z.type === 'straight')
    .map((z) => (z.startDistance + z.endDistance) / 2)
  const brakingEntryCuts = sorted
    .filter((z, i) => {
      if (z.type !== 'braking') return false
      const prev = sorted[(i - 1 + sorted.length) % sorted.length]
      return (
        prev !== z &&
        (prev.type === 'cornering' || prev.type === 'acceleration')
      )
    })
    .map((z) => z.startDistance)
  // Compact hotspots are atomic: zone-derived cuts inside are dropped so
  // the hotspot survives as ONE section. Mega-hotspots keep interior cuts.
  const insideHotspot = (c: number): boolean =>
    hotspots.some(
      (h) =>
        h.endDistance - h.startDistance <= MAX_ATOMIC_HOTSPOT_LENGTH_METERS &&
        c > h.startDistance + 1 &&
        c < h.endDistance - 1
    )
  const zoneCuts = [...straightCuts, ...brakingEntryCuts].filter(
    (c) => !insideHotspot(c)
  )
  const hotspotCuts = hotspots.flatMap((h) => [h.startDistance, h.endDistance])
  const rawCuts = [...zoneCuts, ...hotspotCuts].sort((a, b) => a - b)
  // Collapse near-duplicate cuts (a hotspot edge can coincide with a zone cut).
  const cuts = rawCuts.filter((c, i) => i === 0 || c - rawCuts[i - 1] > 1)
  if (cuts.length === 0) return []

  const complexZones = sorted.filter((z) => z.type !== 'straight')
  if (complexZones.length === 0 && hotspots.length === 0) return []

  const zoneMid = (z: TrackZone): number =>
    (z.startDistance + z.endDistance) / 2

  const sections: TimeSection[] = []
  for (let i = 0; i < cuts.length; i++) {
    const start = cuts[i]
    const end = cuts[(i + 1) % cuts.length]
    const wraps = i === cuts.length - 1
    // Wrap sections list zones in track order AROUND start/finish: the run-up
    // to the line first, then the zones after it. A plain filter would return
    // them in startDistance order instead.
    const zoneIds = wraps
      ? [
          ...complexZones.filter((z) => zoneMid(z) >= start),
          ...complexZones.filter((z) => zoneMid(z) < end),
        ].map((z) => z.id)
      : complexZones
          .filter((z) => inSection(zoneMid(z), start, end, wraps))
          .map((z) => z.id)
    sections.push({
      id: `section-${i}`,
      startDistance: start,
      endDistance: end,
      zoneIds,
      wraps,
    })
  }
  return sections
}

/** Net time delta over one section for one lap. Positive = time lost vs reference. */
export interface SectionLapDelta {
  sectionId: string
  lapId: string
  deltaMs: number
}

/**
 * Net delta per (lap, section): smoothed drift at the end cut minus at the
 * start cut. Wrap sections sum the leg to the finish line and the leg from
 * the start line. All drift results share the reference lap's distance frame,
 * so the same cut distance is consistent across laps.
 */
export function computeSectionDeltas(
  sections: TimeSection[],
  drifts: ReadonlyMap<string, TimeDriftResult>
): SectionLapDelta[] {
  const out: SectionLapDelta[] = []
  for (const [lapId, result] of drifts) {
    const points = result.points
    if (points.length === 0) continue
    const lapEnd = points[points.length - 1].distanceMeters
    for (const s of sections) {
      const deltaMs = s.wraps
        ? interpolateTimeDrift(result, lapEnd) -
          interpolateTimeDrift(result, s.startDistance) +
          (interpolateTimeDrift(result, s.endDistance) -
            interpolateTimeDrift(result, 0))
        : interpolateTimeDrift(result, s.endDistance) -
          interpolateTimeDrift(result, s.startDistance)
      if (!Number.isFinite(deltaMs)) continue
      out.push({ sectionId: s.id, lapId, deltaMs })
    }
  }
  return out
}

export interface SectionBadgeOptions {
  /** Minimum |median delta| for a section to earn a badge (ms). */
  minDeltaMs: number
  /** Never show more than this many badges (largest magnitudes win). */
  maxBadges: number
}

export const DEFAULT_SECTION_BADGE_OPTIONS: SectionBadgeOptions = {
  minDeltaMs: 100,
  maxBadges: 4,
}

/** A badge-worthy section. deltaMs positive = time lost vs reference. */
export interface SectionBadge {
  sectionId: string
  /** Median net delta of the agreeing laps (the lap's own delta when only one). */
  deltaMs: number
  agreeingLapCount: number
  /** Laps whose sign matched the consensus — the ones behind deltaMs. */
  agreeingLapIds: string[]
}

/**
 * One badge per section where laps commonly gain or lose: with multiple
 * comparison laps a strict sign-majority is required and the median of the
 * agreeing laps is shown; disagreement means no badge (an honest "it
 * depends" beats a misleading number).
 *
 * Ranking is losses-first (field rule 2026-07-29: "biggest time lost
 * prioritised"): every lost-time section outranks every gained-time one,
 * magnitude ordering within each group. With the cap at four, a big gain
 * only earns a badge when fewer than four sections lose time.
 *
 * The minDeltaMs floor applies to the FULL-SECTION median; focusing later
 * re-reads the displayed value over a narrower window, so a badge's shown
 * number can dip slightly below the admission floor. That is expected —
 * do not re-apply the floor after focusing.
 */
export function summarizeSectionDeltas(
  deltas: SectionLapDelta[],
  options: SectionBadgeOptions = DEFAULT_SECTION_BADGE_OPTIONS
): SectionBadge[] {
  const bySection = new Map<string, SectionLapDelta[]>()
  for (const d of deltas) {
    const list = bySection.get(d.sectionId)
    if (list) list.push(d)
    else bySection.set(d.sectionId, [d])
  }

  const badges: SectionBadge[] = []
  for (const [sectionId, entries] of bySection) {
    let agreeing: SectionLapDelta[]
    if (entries.length === 1) {
      agreeing = entries
    } else {
      const positive = entries.filter((e) => e.deltaMs > 0)
      const negative = entries.filter((e) => e.deltaMs < 0)
      if (positive.length > entries.length / 2) agreeing = positive
      else if (negative.length > entries.length / 2) agreeing = negative
      else continue
    }
    const deltaMs = median(agreeing.map((e) => e.deltaMs))
    if (Math.abs(deltaMs) < options.minDeltaMs) continue
    badges.push({
      sectionId,
      deltaMs,
      agreeingLapCount: agreeing.length,
      agreeingLapIds: agreeing.map((e) => e.lapId),
    })
  }

  return badges
    .sort((a, b) => {
      const aLoss = a.deltaMs > 0 ? 1 : 0
      const bLoss = b.deltaMs > 0 ? 1 : 0
      if (aLoss !== bLoss) return bLoss - aLoss
      return Math.abs(b.deltaMs) - Math.abs(a.deltaMs)
    })
    .slice(0, options.maxBadges)
}

export interface SectionFocusOptions {
  /**
   * The focused window must capture at least this fraction of the
   * section's net delta. Lower = more specific windows; the discarded
   * remainder is same-direction bleed that isn't shown.
   */
  captureFraction: number
  /**
   * Trap guard: the two excluded tails COMBINED may hide no countermovement
   * (opposite-direction excursion of the median curve) larger than this
   * fraction of the badge's |delta| — one shared budget, not one per tail,
   * so the displayed delta can never exceed the section's honest net by
   * more than the tolerance. This is what stops a window from cherry-
   * picking the exit loss out of a brake-late trade: the entry gain would
   * be a hidden countermovement, so the window is forced to include it and
   * the displayed number stays netted.
   */
  counterToleranceFraction: number
  /** Absolute floor for the trap guard (ms): noise-level wiggles in the
   * excluded tails shouldn't pin the window wide. */
  counterToleranceMs: number
  /**
   * After the minimal capturing window is found, its edges keep extending
   * while the adjacent sample's rate is at least this fraction of the
   * window's own density — a burst is never sliced mid-burst just because
   * the capture target was already met, and a uniform bleed (where every
   * neighbor matches the density) extends back to the full section, which
   * is the honest display for it.
   */
  extendRateFractionOfCore: number
  /** Sampling step for the median-curve scan. */
  sampleStepMeters: number
  /** Never focus below this length (badge and boundary ticks need room). */
  minCoreLengthMeters: number
}

export const DEFAULT_SECTION_FOCUS_OPTIONS: SectionFocusOptions = {
  captureFraction: 0.7,
  counterToleranceFraction: 0.1,
  counterToleranceMs: 20,
  extendRateFractionOfCore: 0.5,
  sampleStepMeters: 10,
  minCoreLengthMeters: 30,
}

/** A badge focused onto the stretch where its delta actually accrues. */
export interface FocusedSectionBadge extends SectionBadge {
  /** Display window start, meters from lap start. */
  coreStartDistance: number
  /** Display window end. Less than the start when the window wraps. */
  coreEndDistance: number
  /** True when the focused window itself crosses start/finish. */
  coreWraps: boolean
}

/**
 * Wrap-aware reader of drift curves over one section, in the section's
 * UNWRAPPED frame: positions run monotonically from the start cut to the
 * end cut, continuing past the finish line for wrap sections. This is the
 * primitive for any analysis of the drift shape inside a section — the
 * focus search below consumes it, and future badge classifiers (e.g.
 * "loss followed by slow acceleration") should too, instead of
 * re-deriving the seam math.
 */
export interface SectionCurveSampler {
  /** Sample distances from the start cut to the end cut, a step apart,
   * both endpoints included (unwrapped frame). */
  positions: number[]
  /** One curve's drift at an unwrapped-frame distance. */
  valueAt(curve: TimeDriftResult, distance: number): number
  /** Pointwise median of the sampled curves at an unwrapped-frame distance. */
  medianAt(distance: number): number
}

/**
 * Build a sampler over `curves` for `section`. `lapEnd` is the wrap seam
 * (shortest curve's final distance — the shared frame every drift result
 * uses); drift past the seam is shifted by each curve's full-lap delta so
 * a wrap section's curve stays continuous across start/finish and one
 * walk handles both section kinds. Returns null when there is nothing to
 * sample (no curves, or a wrap section without a known seam).
 */
export function createSectionCurveSampler(
  section: TimeSection,
  curves: TimeDriftResult[],
  lapEnd: number,
  stepMeters: number
): SectionCurveSampler | null {
  if (curves.length === 0 || (section.wraps && lapEnd <= 0)) return null
  const valueAt = (c: TimeDriftResult, d: number): number =>
    section.wraps && d > lapEnd
      ? interpolateTimeDrift(c, d - lapEnd) +
        (interpolateTimeDrift(c, lapEnd) - interpolateTimeDrift(c, 0))
      : interpolateTimeDrift(c, d)
  const medianAt = (d: number): number =>
    median(curves.map((c) => valueAt(c, d)))
  const start = section.startDistance
  const end = section.wraps ? section.endDistance + lapEnd : section.endDistance
  const positions: number[] = []
  for (let d = start; d < end; d += stepMeters) positions.push(d)
  positions.push(end)
  return { positions, valueAt, medianAt }
}

/**
 * Largest countermovement (peak-to-trough descent of v) inside the
 * would-be excluded prefix [0..i] / suffix [i..n-1]. prefixCounter is
 * non-decreasing and suffixCounter non-increasing in i, so widening a
 * window never increases what its tails hide — edge extension after the
 * search can only tighten the guard, never violate it.
 */
function computeCountermovementBounds(v: number[]): {
  prefixCounter: number[]
  suffixCounter: number[]
} {
  const n = v.length
  const prefixCounter: number[] = new Array<number>(n).fill(0)
  let runMax = v[0]
  let descent = 0
  for (let i = 1; i < n; i++) {
    runMax = Math.max(runMax, v[i])
    descent = Math.max(descent, runMax - v[i])
    prefixCounter[i] = descent
  }
  const suffixCounter: number[] = new Array<number>(n).fill(0)
  let runMin = v[n - 1]
  descent = 0
  for (let i = n - 2; i >= 0; i--) {
    descent = Math.max(descent, v[i] - runMin)
    runMin = Math.min(runMin, v[i])
    suffixCounter[i] = descent
  }
  return { prefixCounter, suffixCounter }
}

/**
 * Shortest window [a, b] capturing at least `target` of the rise of v
 * while the countermovement hidden in BOTH excluded tails COMBINED stays
 * within `guard` — the tails share one budget, so the window's rise can
 * never exceed the full range's net by more than `guard` no matter how
 * the hidden movement splits across the two ends. Ties go to the larger
 * capture; the full range always qualifies.
 */
function findShortestCapturingWindow(
  v: number[],
  positions: number[],
  opts: { target: number; guard: number; minLengthMeters: number }
): { a: number; b: number } {
  const n = v.length
  const { prefixCounter, suffixCounter } = computeCountermovementBounds(v)
  let bestA = 0
  let bestB = n - 1
  let bestLen = positions[n - 1] - positions[0]
  let bestCapture = v[n - 1] - v[0]
  for (let a = 0; a < n - 1 && prefixCounter[a] <= opts.guard; a++) {
    for (let b = a + 1; b < n; b++) {
      if (positions[b] - positions[a] < opts.minLengthMeters) continue
      if (prefixCounter[a] + suffixCounter[b] > opts.guard) continue
      if (v[b] - v[a] < opts.target) continue
      const len = positions[b] - positions[a]
      const capture = v[b] - v[a]
      if (
        len < bestLen - 1e-9 ||
        (len <= bestLen + 1e-9 && capture > bestCapture)
      ) {
        bestA = a
        bestB = b
        bestLen = len
        bestCapture = capture
      }
      break // longer b only lengthens this a's window
    }
  }
  return { a: bestA, b: bestB }
}

/**
 * Grow [a, b] while the adjacent sample's rate is at least `rateFraction`
 * of the window's own density, so the capture target can't slice through
 * the middle of a burst. A uniform bleed extends back to the full range —
 * honest, since the time genuinely accrues everywhere there.
 */
function extendWindowEdges(
  v: number[],
  positions: number[],
  window: { a: number; b: number },
  rateFraction: number
): { a: number; b: number } {
  const n = v.length
  let { a, b } = window
  for (;;) {
    const density = (v[b] - v[a]) / Math.max(positions[b] - positions[a], 1e-9)
    const threshold = rateFraction * density
    const leftRate =
      a > 0 ? (v[a] - v[a - 1]) / (positions[a] - positions[a - 1]) : -Infinity
    const rightRate =
      b < n - 1
        ? (v[b + 1] - v[b]) / (positions[b + 1] - positions[b])
        : -Infinity
    if (leftRate >= threshold && leftRate >= rightRate) {
      a--
    } else if (rightRate >= threshold) {
      b++
    } else {
      break
    }
  }
  return { a, b }
}

/**
 * Focus each badge onto the SHORTEST window that captures most (the
 * capture fraction) of the section's net delta — where the time is most
 * concentrated — then extend its edges while the neighboring curve is
 * still comparably hot (extendRateFractionOfCore of the window's density)
 * so a burst is shown whole. Attribution stays at full-section
 * granularity: ranking and consensus ran on the whole entry-to-exit
 * window, so a late-braking gain that costs the exit still nets before a
 * section earns a badge. The trap survives on display through the
 * countermovement guard: the window search may discard same-direction
 * bleed freely, but the two excluded tails COMBINED may never hide
 * opposite-direction movement beyond the guard tolerance — cherry-picking
 * the flattering half of a trade is structurally impossible, and the
 * displayed delta can never exceed the section's honest net by more than
 * the guard tolerance (one shared budget, not one per tail).
 *
 * The search runs on the median of the badge's AGREEING laps — the same
 * population the value is re-read from over the focused window — so the
 * guard bounds the number actually shown, and the number remains exactly
 * what the drift chart shows between the two guides.
 */
export function focusSectionBadges(
  badges: SectionBadge[],
  sections: TimeSection[],
  drifts: ReadonlyMap<string, TimeDriftResult>,
  options: SectionFocusOptions = DEFAULT_SECTION_FOCUS_OPTIONS
): FocusedSectionBadge[] {
  const sectionsById = new Map(sections.map((s) => [s.id, s]))
  // The wrap seam stays the shared distance frame (shortest curve across
  // ALL laps), matching computeSectionDeltas and findRateHotspots.
  const allCurves = [...drifts.values()].filter((d) => d.points.length >= 2)
  const lapEnd =
    allCurves.length > 0
      ? Math.min(
          ...allCurves.map((d) => d.points[d.points.length - 1].distanceMeters)
        )
      : 0

  const out: FocusedSectionBadge[] = []
  for (const badge of badges) {
    const section = sectionsById.get(badge.sectionId)
    if (!section) continue
    const unfocused: FocusedSectionBadge = {
      ...badge,
      coreStartDistance: section.startDistance,
      coreEndDistance: section.endDistance,
      coreWraps: section.wraps,
    }
    const agreeingCurves = badge.agreeingLapIds
      .map((id) => drifts.get(id))
      .filter((c): c is TimeDriftResult => c != null && c.points.length >= 2)
    const sampler = createSectionCurveSampler(
      section,
      agreeingCurves,
      lapEnd,
      options.sampleStepMeters
    )
    if (!sampler) {
      out.push(unfocused)
      continue
    }
    const { positions, valueAt, medianAt } = sampler
    const n = positions.length

    // Orient so the badge's direction is "up" (v rises where time moves
    // the way the badge reports).
    const sign = badge.deltaMs >= 0 ? 1 : -1
    const v = positions.map((d) => sign * medianAt(d))
    const net = v[n - 1] - v[0]
    if (n < 3 || net <= 0) {
      // Defensive: every agreeing curve moves the badge's way over the
      // section, so a non-positive net means the section is too short to
      // sample meaningfully — nothing trustworthy to focus on.
      out.push(unfocused)
      continue
    }
    const guard = Math.max(
      options.counterToleranceMs,
      options.counterToleranceFraction * Math.abs(badge.deltaMs)
    )
    const core = extendWindowEdges(
      v,
      positions,
      findShortestCapturingWindow(v, positions, {
        target: options.captureFraction * net,
        guard,
        minLengthMeters: options.minCoreLengthMeters,
      }),
      options.extendRateFractionOfCore
    )
    const coreStart = positions[core.a]
    const coreEnd = positions[core.b]

    const coreWraps = section.wraps && coreStart < lapEnd && coreEnd > lapEnd
    out.push({
      ...badge,
      deltaMs: median(
        agreeingCurves.map((c) => valueAt(c, coreEnd) - valueAt(c, coreStart))
      ),
      coreStartDistance:
        section.wraps && coreStart >= lapEnd ? coreStart - lapEnd : coreStart,
      coreEndDistance:
        section.wraps && coreEnd > lapEnd ? coreEnd - lapEnd : coreEnd,
      coreWraps,
    })
  }
  return out
}
